import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { 
  Phone, 
  Send,
  Loader2,
  CheckCircle,
  Car
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE_NUMBER = "(940) 337-9761";
const PHONE_LINK = "tel:+19403379761";

const serviceOptions = [
  "Oil Change",
  "Brake Repair",
  "Engine Diagnostics",
  "Transmission Service",
  "Tune-Up",
  "AC & Heating Repair",
  "Battery Replacement",
  "General Maintenance",
  "Other",
];

export default function QuotePage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [serviceError, setServiceError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    vehicle_make_model: "",
    service_needed: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service_needed: value }));
    setServiceError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.service_needed) {
      setServiceError("Please select the service you need.");
      toast.error("Please select the service you need.");
      return;
    }
    setServiceError("");
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/quote`, formData);
      toast.success("Quote request submitted! We'll contact you shortly.");
      setIsSubmitted(true);
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch (error) {
      console.error("Error submitting quote request:", error);
      toast.error("Failed to submit. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div data-testid="quote-success" className="pt-24 min-h-screen bg-background">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <Card className="max-w-2xl mx-auto bg-card border-border">
            <CardContent className="p-8 md:p-12 text-center">
              <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="font-heading text-3xl md:text-4xl uppercase text-foreground mb-4">
                Quote Request <span className="text-primary">Received!</span>
              </h1>
              <p className="text-muted-foreground mb-8">
                Thank you for your request! We'll review your information and get back to you 
                within 24 hours. For immediate assistance, give us a call.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={PHONE_LINK} data-testid="success-call-btn">
                  <Button className="cta-primary flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    {PHONE_NUMBER}
                  </Button>
                </a>
                <Link to="/" data-testid="success-home-btn">
                  <Button className="cta-secondary">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="quote-page" className="pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-card">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-6xl uppercase tracking-tight text-foreground mb-6 animate-fade-in-up">
              Request a <span className="text-primary">Free Quote</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-in-up delay-100">
              Fill out the form below and we'll get back to you with a quote. 
              No obligation, no pressure – just honest pricing.
            </p>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2 animate-fade-in-up">
              <Card className="bg-card border-border">
                <CardContent className="p-6 md:p-8">
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                    {/* Personal Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-foreground">Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="Your full name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          data-testid="quote-name-input"
                          className="bg-muted/50 border-border focus:border-primary h-12"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-foreground">Phone *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="(XXX) XXX-XXXX"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          data-testid="quote-phone-input"
                          className="bg-muted/50 border-border focus:border-primary h-12"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-foreground">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="quote-email-input"
                        className="bg-muted/50 border-border focus:border-primary h-12"
                      />
                    </div>

                    {/* Vehicle Info */}
                    <div className="pt-4 border-t border-border">
                      <h3 className="font-heading text-lg uppercase text-foreground mb-4 flex items-center gap-2">
                        <Car className="w-5 h-5 text-primary" />
                        Vehicle Information
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="vehicle_make_model" className="text-foreground">
                            Vehicle Make/Model *
                          </Label>
                          <Input
                            id="vehicle_make_model"
                            name="vehicle_make_model"
                            type="text"
                            placeholder="e.g., 2020 Toyota Camry"
                            value={formData.vehicle_make_model}
                            onChange={handleChange}
                            required
                            data-testid="quote-vehicle-input"
                            className="bg-muted/50 border-border focus:border-primary h-12"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="service_needed" className="text-foreground">
                            Service Needed *
                          </Label>
                          <Select
                            value={formData.service_needed}
                            onValueChange={handleServiceChange}
                            required
                          >
                            <SelectTrigger
                              data-testid="quote-service-select"
                              className="bg-muted/50 border-border focus:border-primary h-12"
                            >
                              <SelectValue placeholder="Select a service" />
                            </SelectTrigger>
                            <SelectContent>
                              {serviceOptions.map((service) => (
                                <SelectItem
                                  key={service}
                                  value={service}
                                  data-testid={`quote-service-option-${service
                                    .toLowerCase()
                                    .replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                  {service}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {serviceError && (
                            <p
                              data-testid="quote-service-error"
                              className="text-sm text-red-400"
                            >
                              {serviceError}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-foreground">
                        Additional Details (Optional)
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Describe your issue or what service you need..."
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        data-testid="quote-message-input"
                        className="bg-muted/50 border-border focus:border-primary resize-none"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="quote-submit-btn"
                      className="cta-primary w-full flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Submit Quote Request
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6 animate-fade-in-up delay-200">
              {/* Quick Call CTA */}
              <Card className="bg-primary border-0">
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl uppercase text-white mb-3">
                    Need Help Now?
                  </h3>
                  <p className="text-blue-100 mb-4 text-sm">
                    For immediate assistance, give us a call. We're happy to help!
                  </p>
                  <a href={PHONE_LINK} data-testid="quote-sidebar-call-btn">
                    <Button className="w-full bg-white text-primary hover:bg-gray-100 font-bold flex items-center justify-center gap-2">
                      <Phone className="w-5 h-5" />
                      {PHONE_NUMBER}
                    </Button>
                  </a>
                </CardContent>
              </Card>

              {/* What to Expect */}
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg uppercase text-foreground mb-4">
                    What to Expect
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">
                        We'll review your request within 24 hours
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">
                        Get a clear, no-obligation estimate
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">
                        No hidden fees or surprise charges
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">
                        Schedule your appointment at your convenience
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
